import { useState, useEffect } from 'react';
import { assemblyService } from 'services/api';
import AvisoModal from './AvisoModal';
import styles from './ReportDataModal.module.css';

// `error`: motivo por el que NO se pudo generar el PDF; se muestra DENTRO del modal (la pantalla que lo abre queda tapada por él).
export default function ReportDataModal({ assembly, onClose, onGenerate, generating, error }) {
  const [form, setForm] = useState({
    presidente:        '',
    secretario:        '',
    lugar:             assembly?.property?.address || '',
    convocatoria:      'Correo electrónico enviado a los propietarios del conjunto.',
    // Precarga el orden del día que el admin fijó en la asamblea (editable).
    orden_del_dia:     assembly?.orden_del_dia || '',
    observaciones:     '',
    firma_digital:     false,
    firma_presidente:  '',
    firma_secretario:  '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Presidente/secretario desde Elecciones: el ganador del cargo CERRADO
  // marcado con ese "rol en el acta". Solo llena el campo si sigue vacío
  // (nunca pisa lo que el admin ya escribió) y siempre queda editable. Si no
  // hubo elección o no hay ganador, el campo queda vacío para escribirlo.
  const [origen, setOrigen] = useState({}); // { presidente: 'Nombre del cargo', ... }
  useEffect(() => {
    if (!assembly?.id) return;
    let vivo = true;
    assemblyService.positions(assembly.id).then(r => {
      if (!vivo) return;
      const encontrados = {};
      (r.data.positions || []).forEach(p => {
        if (!p.rol_acta || p.status !== 'cerrado') return;
        const ganador = (p.nominations || []).find(n => n.won);
        if (ganador) encontrados[p.rol_acta] = { nombre: ganador.candidate_name, cargo: p.name };
      });
      setForm(f => ({
        ...f,
        presidente: f.presidente || encontrados.presidente?.nombre || '',
        secretario: f.secretario || encontrados.secretario?.nombre || '',
      }));
      setOrigen({ presidente: encontrados.presidente?.cargo, secretario: encontrados.secretario?.cargo });
    }).catch(() => {});
    return () => { vivo = false; };
  }, [assembly?.id]);

  const notaOrigen = (rol) => origen[rol] && form[rol] ? (
    <div className={styles.notaOrigen}>
      <i className={`fas fa-check-to-slot ${styles.iconoOrigen}`} />
      Tomado de la elección de «{origen[rol]}»
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalActa}`}
        onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3><i className={`fas fa-file-pdf ${styles.iconoTitulo}`} />Generar acta de asamblea</h3>
            <div className={styles.subtitulo}>
              Completa la información — el acta se descargará como PDF
            </div>
            <div className={styles.obligatorios}>
              <i className={`fas fa-asterisk ${styles.asterisco}`} />
              Los campos marcados con <strong>*</strong> son obligatorios
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className={`modal-body ${styles.cuerpo}`}>
          {error && <AvisoModal>{error}</AvisoModal>}

          <div className={styles.rejilla}>
            <div className="form-group">
              <label className="form-label">Presidente de asamblea *</label>
              <input className="form-control" value={form.presidente}
                onChange={e => set('presidente', e.target.value)} placeholder="Nombre completo" required />
              {notaOrigen('presidente')}
            </div>
            <div className="form-group">
              <label className="form-label">Secretario de asamblea *</label>
              <input className="form-control" value={form.secretario}
                onChange={e => set('secretario', e.target.value)} placeholder="Nombre completo" required />
              {notaOrigen('secretario')}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Lugar de realización</label>
            <input className="form-control" value={form.lugar}
              onChange={e => set('lugar', e.target.value)} placeholder="Dirección o plataforma virtual" />
          </div>

          <div className="form-group">
            <label className="form-label">Forma de convocatoria</label>
            <input className="form-control" value={form.convocatoria}
              onChange={e => set('convocatoria', e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">Orden del día</label>
            <textarea className="form-control" rows="5" value={form.orden_del_dia}
              onChange={e => set('orden_del_dia', e.target.value)}
              placeholder={"1. Llamado a lista y verificación de quórum\n2. Elección de presidente y secretario\n3. ..."} />
            <div className={styles.nota}>Cada ítem en una línea nueva</div>
          </div>

          <div className="form-group">
            <label className="form-label">Observaciones</label>
            <textarea className="form-control" rows="3" value={form.observaciones}
              onChange={e => set('observaciones', e.target.value)}
              placeholder="Observaciones generales de la asamblea..." />
          </div>

          {/* Tipo de firma */}
          <div className={styles.firma}>
            <label className={`form-label ${styles.firmaEtiqueta}`}>Tipo de firma</label>
            <div className={styles.firmaOpciones}>
              <button type="button"
                className={`btn btn-sm ${!form.firma_digital ? 'btn-primary' : 'btn-secondary'} ${styles.opcionFirma}`}
                onClick={() => set('firma_digital', false)}>
                <i className={`fas fa-pen ${styles.iconoOpcion}`} />Firma física
                <div className={styles.opcionNota}>Espacio en blanco para firmar</div>
              </button>
              <button type="button"
                className={`btn btn-sm ${form.firma_digital ? 'btn-primary' : 'btn-secondary'} ${styles.opcionFirma}`}
                onClick={() => set('firma_digital', true)}>
                <i className={`fas fa-signature ${styles.iconoOpcion}`} />Firma digital
                <div className={styles.opcionNota}>Nombre como firma</div>
              </button>
            </div>
            {form.firma_digital && (
              <div className={styles.firmaCampos}>
                <div className={`form-group ${styles.grupoSinMargen}`}>
                  <label className="form-label">Firma del presidente</label>
                  <input className={`form-control ${styles.cursiva}`} value={form.firma_presidente}
                    onChange={e => set('firma_presidente', e.target.value)}
                    placeholder="Nombre que aparecerá" />
                </div>
                <div className={`form-group ${styles.grupoSinMargen}`}>
                  <label className="form-label">Firma del secretario</label>
                  <input className={`form-control ${styles.cursiva}`} value={form.firma_secretario}
                    onChange={e => set('firma_secretario', e.target.value)}
                    placeholder="Nombre que aparecerá" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={generating}>
            Cancelar
          </button>
          <button type="button" className={`btn btn-primary ${styles.botonGenerar}`}
            disabled={generating || !form.presidente || !form.secretario}
            onClick={() => onGenerate(form)}>
            {generating
              ? <><div className={`spinner ${styles.spinnerGenerando}`} /> Generando PDF...</>
              : <><i className={`fas fa-file-pdf ${styles.iconoPdf}`} />Generar y descargar PDF</>}
          </button>
        </div>
      </div>
    </div>
  );
}
